import { Ban, ExternalLink } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { device } from "@/lib/device";
import { DownloadButton } from "@/components/apps/DownloadButton";

export const AndroidIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M17.6 9.48l1.84-3.18a.38.38 0 00-.66-.38l-1.86 3.22a11.4 11.4 0 00-9.84 0L5.22 5.92a.38.38 0 00-.66.38L6.4 9.48A10.8 10.8 0 001 18h22a10.8 10.8 0 00-5.4-8.52zM7 15.25a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm10 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z" />
  </svg>
);

export const AppleIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M16.37 12.7c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.89-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.89 2.65 3.24 2.6 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.38.81 1.4-.02 2.29-1.27 3.14-2.53.99-1.45 1.4-2.86 1.42-2.93-.03-.01-2.72-1.04-2.73-4.12zM13.8 5.06c.72-.87 1.2-2.07 1.07-3.27-1.03.04-2.28.69-3.02 1.55-.66.77-1.24 1.99-1.09 3.17 1.15.09 2.32-.58 3.04-1.45z" />
  </svg>
);

export const platformOf = (app) => app.platform || { compat: app.active_version ? "android" : null };
const has = (p) => ({ android: p.compat === "android" || p.compat === "both", iphone: (p.compat === "iphone" || p.compat === "both") && !!p.iphone_url });

// Shown above the logo on cards and presentation pages.
export const PlatformBadge = ({ app, testId }) => {
  const { t } = useI18n();
  const { compat } = platformOf(app);
  if (!compat) return null;
  return (
    <p className="inline-flex max-w-full flex-wrap items-center gap-1.5 self-start rounded-full border border-emerald-400/40 bg-emerald-950/40 px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-wider text-emerald-100"
      data-testid={testId} data-compat={compat}>
      {compat !== "iphone" && <AndroidIcon />}{compat !== "android" && <AppleIcon />}
      <span className="min-w-0 break-words">{t(`plat.${compat}`)}</span>
    </p>
  );
};

const NotAvailable = ({ k, testId }) => {
  const { t } = useI18n();
  return (
    <p className="inline-flex max-w-full items-center gap-2 rounded-full border border-neutral-600 bg-black/40 px-4 py-2 text-xs font-semibold text-neutral-300" data-testid={testId}>
      <Ban className="h-4 w-4 shrink-0" /> <span className="min-w-0 break-words">{t(k)}</span>
    </p>
  );
};

const IphoneButton = ({ p, large, testId }) => {
  const { t } = useI18n();
  return (
    <span className="inline-flex min-w-0 max-w-full flex-col items-start gap-1.5">
      <a href={p.iphone_url} target="_blank" rel="noopener noreferrer" className={`btn-gold max-w-full whitespace-normal ${large ? "px-8 py-4 text-sm" : "btn-sm"}`} data-testid={testId}>
        <AppleIcon className={large ? "h-5 w-5" : "h-4 w-4"} /> {t("plat.openIphone")} <ExternalLink className="h-3.5 w-3.5 shrink-0" />
      </a>
      {p.iphone_kind === "web" && <span className="max-w-xs break-words text-[0.7rem] text-neutral-400" data-testid={`${testId}-guide`}>{t("plat.guide")}</span>}
    </span>
  );
};

// Download area adapted to the visitor device (Android → APK, iPhone/iPad → iPhone link, desktop → both).
export const PlatformDownload = ({ app, large = false, testId = "download-btn", autoStart = false }) => {
  const p = platformOf(app);
  const { android, iphone } = has(p);
  const d = device();
  const apk = <DownloadButton app={app} large={large} autoStart={autoStart} testId={testId} />;
  const ios = <IphoneButton p={p} large={large} testId={`${testId}-iphone`} />;
  if (d === "ios") return iphone ? ios : android ? <NotAvailable k="plat.notIphone" testId={`${testId}-not-iphone`} /> : apk;
  if (d === "android") return android ? apk : iphone ? <NotAvailable k="plat.notAndroid" testId={`${testId}-not-android`} /> : apk;
  if (!android && !iphone) return apk;
  return <span className="inline-flex max-w-full flex-wrap items-start gap-3">{android && apk}{iphone && ios}</span>;
};

export const PLATFORM_CHIPS = ["all", "android", "iphone"];
export const byPlatform = (apps, plat) => apps && (plat === "all" ? apps : apps.filter((a) => has(platformOf(a))[plat]));

export const PlatformChips = ({ apps, value, onChange, prefix }) => {
  const { t } = useI18n();
  if (!apps?.length) return null;
  return (
    <div className="mb-6 flex flex-wrap gap-2" role="tablist" data-testid={`${prefix}-platform-chips`}>
      {PLATFORM_CHIPS.map((c) => (
        <button key={c} type="button" role="tab" aria-selected={value === c} onClick={() => onChange(c)}
          className={`inline-flex max-w-full items-center gap-1.5 whitespace-normal break-words rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${value === c ? "border-emerald-400 bg-emerald-400 text-black" : "border-emerald-400/30 text-neutral-200 hover:border-emerald-400"}`}
          data-testid={`${prefix}-platform-${c}`}>
          {c === "android" && <AndroidIcon />}{c === "iphone" && <AppleIcon />} {t(`plat.chip.${c}`)}
        </button>
      ))}
    </div>
  );
};
